import { motion } from "framer-motion";

/**
 * FadeInDirection — viewport-triggered cinematic reveal.
 *
 * Used by the new TripDetail layout (Feb 2026 redesign) to give each
 * section a dramatic entrance:
 *   • `direction="left"` → slides in from -80 px, rotates from -2°
 *   • `direction="right"` → slides in from +80 px, rotates from +2°
 *   • `direction="up"` (default) → slides up from +40 px (no rotation)
 *
 * Each section in TripDetail alternates left/right for a "wave" reading
 * rhythm where each card swings into place from a different side. The
 * right-column cards use "up" for a softer reveal that doesn't compete
 * with the left column's motion.
 *
 * Motion details (b99.10.x — bumped after field feedback "podemos pôr
 * os movimentos das secções mais dramáticos?"):
 *   - 80 px displacement (was 32) → much more visible swing
 *   - Scale 0.92 → 1 → cards feel like they "pop in" instead of slide
 *   - 2° rotation on L/R variants → subtle 3D fold effect
 *   - Spring transition with overshoot (stiffness 90, damping 14) →
 *     the card slightly overshoots its resting position before
 *     settling, giving a kinetic, alive feel
 *   - margin: "-100px" on viewport so cards start animating just
 *     before they enter the visible area
 *
 * Animation fires once per element (`viewport.once: true`) so users
 * don't see a re-trigger if they scroll back up — keeps the page from
 * feeling jittery on long-form reads.
 */
export function FadeInDirection({
  children,
  direction = "up",
  delay = 0,
  className = "",
  testId,
}) {
  const xOffset = direction === "left" ? -80 : direction === "right" ? 80 : 0;
  const yOffset = direction === "up" ? 40 : 0;
  // Subtle rotation only on horizontal slides — gives the card a 3D
  // "fold-in" feel without making the page look like a slideshow.
  const rotateOffset =
    direction === "left" ? -2 : direction === "right" ? 2 : 0;
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: xOffset,
        y: yOffset,
        scale: 0.92,
        rotate: rotateOffset,
      }}
      whileInView={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{
        type: "spring",
        stiffness: 90,
        damping: 14,
        delay,
        // The opacity tween is shorter than the spring so the content
        // fades in fully before the overshoot wobble starts — prevents
        // the text from looking blurry/anchorless during the bounce.
        opacity: { duration: 0.45, delay, ease: "easeOut" },
      }}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}
